import { create } from "zustand"
import { immer } from "zustand/middleware/immer"
import { z } from "zod"
import type { BulkUpload, BulkUploadTableFilters, UploadTableStatus } from "@/src/types/bulkUploadsModel"
import type { GridSortModel, GridRowSelectionModel } from "@mui/x-data-grid"
import type { PaginationState, BaseStoreState } from "../types"
import { useBulkUpload, useGetBulkUploads } from "@/src/services/bulkUploads"
import { useAuthStore } from "./auth.slice"

// Zod schemas for validation
const UploadTableStatusSchema = z.enum(["PROCESSING", "COMPLETED", "FAILED"])

const BulkUploadTableFiltersSchema = z.object({
    jobId: z
        .array(
            z.object({
                label: z.string(),
                value: z.string(),
            })
        )
        .optional(),
    status: z.array(z.string()).optional(),
})

const BulkUploadSchema = z.object({
    id: z.string(),
    createdBy: z.object({
        label: z.string(),
        value: z.string(),
    }),
    createdAt: z.string().nullable(),
    fileUrl: z.string(),
    source: z.string(),
    status: UploadTableStatusSchema,
    totalRecords: z.number().min(0),
    success: z.number().min(0),
    result: z.string(),
    mandate: z.object({
        label: z.string(),
        value: z.string(),
    }),
    errors: z.number().min(0),
    duplicates: z.number().min(0),
    reportUrl: z.string().optional(),
    recruiters: z.array(
        z.object({
            label: z.string(),
            value: z.string(),
        })
    ),
    comments: z.string().optional(),
})

// Store state interface
export interface BulkUploadStoreState extends BaseStoreState {
    uploads: BulkUpload[]
    formData: {
        file: File | null
        jobId: string | null
        recruiters: string[]
        comments: string
    }
    filters: BulkUploadTableFilters
    pagination: PaginationState
    sortModel: GridSortModel
    searchQuery: string
    selectedRows: GridRowSelectionModel
    columnVisibility: GridColumnVisibilityModel
    tabData: { [key: string]: number } | undefined
}

// Store actions interface
export interface BulkUploadStoreActions {
    // Upload management
    uploadCsv: (data: { file: File; jobId: string; recruiters: string[]; comments: string }) => Promise<void>
    fetchUploads: (filters?: BulkUploadTableFilters) => Promise<void>
    deleteUpload: (id: string) => Promise<void>

    // Form management
    setFormData: (data: Partial<BulkUploadStoreState["formData"]>) => void
    resetForm: () => void

    // Filters and pagination
    updateFilters: (filters: Partial<BulkUploadTableFilters>) => void
    setPagination: (pagination: Partial<PaginationState>) => void
    setSortModel: (sortModel: GridSortModel) => void
    setSearchQuery: (query: string) => void
    setSelectedRows: (rows: GridRowSelectionModel) => void
    setColumnVisibility: (visibility: GridColumnVisibilityModel) => void
}

export type BulkUploadStore = BulkUploadStoreState & BulkUploadStoreActions

// Default form data
const defaultFormData = {
    file: null,
    jobId: null,
    recruiters: [],
    comments: "",
}

// Default filters
const defaultFilters: BulkUploadTableFilters = {
    jobId: undefined,
    status: undefined,
}

export const useBulkUploadStore = create<BulkUploadStore>()(
    immer((set, get) => ({
        // State
        uploads: [],
        formData: defaultFormData,
        filters: defaultFilters,
        pagination: { page: 0, pageSize: 40 },
        sortModel: [],
        searchQuery: "",
        selectedRows: [],
        columnVisibility: { category: false },
        tabData: undefined,
        loading: false,
        error: null,

        // Actions
        uploadCsv: async (data: { file: File; jobId: string; recruiters: string[]; comments: string }) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                const { user } = useAuthStore.getState()

                if (!user?.activeWorkspace?.id) {
                    throw new Error("No active workspace found")
                }

                // Form data validation can be added later if needed

                // Use the existing useBulkUpload hook logic
                const response = await useBulkUpload(data, user.activeWorkspace.id)

                set((state) => {
                    state.loading = false
                    state.error = null
                    state.formData = defaultFormData // Reset form after successful upload
                })

                return response
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to upload CSV"
                })
                throw error
            }
        },

        fetchUploads: async (filters?: BulkUploadTableFilters) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                const { pagination, sortModel } = get()
                const targetFilters = filters || get().filters
                const { user } = useAuthStore.getState()

                if (!user?.activeWorkspace?.id) {
                    throw new Error("No active workspace found")
                }

                // Use the existing useGetBulkUploads hook logic
                const { uploads, uploadsCount, uploadsLoading, tabData } = useGetBulkUploads(
                    pagination.page,
                    pagination.pageSize,
                    sortModel,
                    "", // searchQuery
                    [], // filterOptions
                    targetFilters
                )

                // Uploads data validation can be added later if needed
                set((state) => {
                    state.uploads = uploads
                    state.tabData = tabData
                    state.loading = uploadsLoading
                    state.error = null
                })
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to fetch uploads"
                })
            }
        },

        deleteUpload: async (id: string) => {
            set((state) => {
                state.loading = true
                state.error = null
            })

            try {
                // This would typically call an API to delete upload
                // For now, we'll remove from local state

                set((state) => {
                    state.uploads = state.uploads.filter((u) => u.id !== id)
                    state.loading = false
                    state.error = null
                })
            } catch (error) {
                set((state) => {
                    state.loading = false
                    state.error = error instanceof Error ? error.message : "Failed to delete upload"
                })
                throw error
            }
        },

        setFormData: (data: Partial<BulkUploadStoreState["formData"]>) => {
            set((state) => {
                state.formData = { ...state.formData, ...data }
            })
        },

        resetForm: () => {
            set((state) => {
                state.formData = { ...defaultFormData }
            })
        },

        updateFilters: (filters: Partial<BulkUploadTableFilters>) => {
            set((state) => {
                state.filters = { ...state.filters, ...filters }
                // Reset pagination to first page when filters change to avoid showing empty results
                // This ensures users always see data after applying filters
                state.pagination.page = 0
                state.error = null
            })
        },

        setPagination: (pagination: Partial<PaginationState>) => {
            set((state) => {
                state.pagination = { ...state.pagination, ...pagination }
            })
        },

        setSortModel: (sortModel: GridSortModel) => {
            set((state) => {
                state.sortModel = sortModel
            })
        },

        setSearchQuery: (query: string) => {
            set((state) => {
                state.searchQuery = query
            })
        },

        setSelectedRows: (rows: GridRowSelectionModel) => {
            set((state) => {
                state.selectedRows = rows
            })
        },

        setColumnVisibility: (visibility: GridColumnVisibilityModel) => {
            set((state) => {
                state.columnVisibility = visibility
            })
        },
    }))
)
